import { useSearchParams } from 'react-router-dom';
import { Check, Filter, X } from 'lucide-react';
import { RenderIf } from '@/components/RenderIf';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import getMetricStatusFilterFromSearchParams from '../helpers/getMetricStatusFilterFromSearchParams';
import type { MetricStatusFilter } from '../types';
import type { StatusFilterOption } from './types';
import { FilterButton } from './styled';

const statusOptions: StatusFilterOption[] = [
    { value: 'active', label: 'Active' },
    { value: 'deprecated', label: 'Deprecated' },
    { value: 'archived', label: 'Archived' },
    { value: 'all', label: 'All statuses' },
];

const getStatusLabel = ({ status }: { status: StatusFilterOption['value'] }): string => {
    return statusOptions.find(option => option.value === status)?.label || 'Status';
};

function StatusFilter() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedStatus = getMetricStatusFilterFromSearchParams({
        value: searchParams.get('status'),
    });
    const showClearButton = selectedStatus !== 'active';

    const handleSelectStatus = ({ status }: { status: MetricStatusFilter }) => {
        const nextParams = new URLSearchParams(searchParams);

        if (status === 'active') {
            nextParams.delete('status');
        } else {
            nextParams.set('status', status);
        }

        setSearchParams(nextParams);
    };

    return (
        <DropdownMenu>
            <FilterButton>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                        <Filter size={14} />
                        <span>{getStatusLabel({ status: selectedStatus })}</span>
                        <RenderIf isTrue={showClearButton}>
                            <X
                                size={14}
                                className="cursor-pointer"
                                onClick={event => {
                                    event.stopPropagation();
                                    handleSelectStatus({ status: 'active' });
                                }}
                            />
                        </RenderIf>
                    </Button>
                </DropdownMenuTrigger>
            </FilterButton>

            <DropdownMenuContent align="end" className="w-56">
                {statusOptions.map(option => {
                    const isSelected = selectedStatus === option.value;
                    return (
                        <DropdownMenuItem
                            key={option.value}
                            onClick={() => handleSelectStatus({ status: option.value })}
                            className="gap-2"
                        >
                            <span className="flex-1">{option.label}</span>
                            <RenderIf isTrue={isSelected}>
                                <Check size={14} />
                            </RenderIf>
                        </DropdownMenuItem>
                    );
                })}

                <RenderIf isTrue={showClearButton}>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={() => handleSelectStatus({ status: 'active' })}
                        className="gap-2"
                    >
                        <X size={14} />
                        <span>Reset to active</span>
                    </DropdownMenuItem>
                </RenderIf>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default StatusFilter;
